import { useEffect, useState } from "react";
import { useParams } from "react-router";

/* eslint-disable react-hooks/exhaustive-deps */
import styles from "./SurveyQuestions.module.css";

import {
  ISurveyQuestion,
  ISurveyQuestionItemGroupsQuestion,
} from "../../libs/interfaces/services";
import { Header } from "../../components/Header";
import { useContextSurveyQuiz } from "../../hooks/useSurveyQuiz";
import { TextItem } from "../../components/TextItem";
import Button from "../../components/Button";
import { DateItem } from "../../components/DateItem";
import { CheckboxItem } from "../../components/CheckboxItem";
import { RadioItem } from "../../components/RadioItem";
import { IntItem } from "../../components/IntItem";
import { orderByItems } from "../../helpers/orderByList";
import useAssistantStore, { AssistantStore } from "../../store/assistantStore";
import { QuestionType } from "../../models/QuestionTypeEnum";
import { linkifyHtml } from "../../helpers/linkifyHtml";
import { QuizProgressBar } from "../../components/QuizProgressBar";
import { useQuestionForm } from "../../hooks/useQuestionForm";
import { useQuestionNavigation } from "../../hooks/useQuestionNavigation";

interface ISurveyQuestions {
  surveyTopic: ISurveyQuestion;
  indexQuiz: number;
}

interface IAskBox {
  title: string;
  subtitle: string;
  text: string;
  indexQuiz: number;
}

export function SurveyQuestions({ surveyTopic, indexQuiz }: ISurveyQuestions) {
  const {
    surveyQuizIndex,
    answerList,
    numberTopics,
    totalSurveyQuestions,
    totalSurveyAnswered,
    surveySubtitle,
    surveyTitle,
  } = useContextSurveyQuiz();

  const { surveyTopicID } = useParams();

  // ── Hooks extraídos ──
  const { register, handleSubmit, watch, control, errors } = useQuestionForm(
    surveyTopic,
    indexQuiz,
    answerList,
    surveyQuizIndex
  );

  const { loading, submitAnswers, handleGoBack, hashID } =
    useQuestionNavigation(surveyTopic, indexQuiz);

  // ── Assistant ──
  const setHashId = useAssistantStore(
    (state: AssistantStore) => state.setHashId
  );
  const deactivateAssistant = useAssistantStore(
    (state: AssistantStore) => state.deactivateAssistant
  );
  const isAssistantActive = useAssistantStore(
    (state: AssistantStore) => state.isActive
  );
  const { idSubQuestion } = useAssistantStore(
    (state: AssistantStore) => state.config
  );

  useEffect(() => {
    setHashId(hashID!);
  }, [hashID]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [surveyQuizIndex]);

  // ── Submit ──
  const onSubmit = handleSubmit(async (data) => {
    await submitAnswers(data);
  });

  // ── Render de cada tipo de questão ──
  const getQuestionType = (
    surveyItem: ISurveyQuestionItemGroupsQuestion,
    isItemActive: boolean
  ) => {
    const commonProps = {
      register,
      watch,
      id: surveyItem.idSubQuestion,
      errors: errors[surveyItem.idSubQuestion],
      text: surveyItem.text,
      isAssistantActive: isItemActive,
    };

    switch (surveyItem.questionType) {
      case QuestionType.Text:
        return <TextItem {...commonProps} />;

      case QuestionType.Int:
        return <IntItem {...commonProps} options={surveyItem.questionItens} />;

      case QuestionType.Date:
        return <DateItem {...commonProps} control={control} />;

      case QuestionType.MultiSelect:
        return (
          <CheckboxItem {...commonProps} options={surveyItem.questionItens} />
        );

      case QuestionType.Radio:
      case QuestionType.Scale:
        return (
          <RadioItem {...commonProps} options={surveyItem.questionItens} />
        );

      default:
        return null;
    }
  };

  return (
    <section
      className={`${styles.container} ${
        isAssistantActive ? styles.containerWithAssistant : ""
      }`}
    >
      <Header handleNavigate={() => handleGoBack(deactivateAssistant)} />

      {(surveyTitle || surveySubtitle) && (
        <div className={styles.surveyBreadcrumb}>
          <span className={styles.breadcrumbDot} />
          {surveyTitle && (
            <span className={styles.breadcrumbTitle}>{surveyTitle}</span>
          )}
          {surveyTitle && surveySubtitle && (
            <span className={styles.breadcrumbSeparator}>›</span>
          )}
          {surveySubtitle && (
            <span className={styles.breadcrumbText}>{surveySubtitle}</span>
          )}
          {numberTopics.length > 1 && (
            <>
              <span className={styles.breadcrumbSeparator}>›</span>
              <span className={styles.breadcrumbTopic}>
                Tópico{" "}
                {numberTopics.findIndex(
                  (t) => t.idTopic === Number(surveyTopicID)
                ) + 1}{" "}
                de {numberTopics.length}
              </span>
            </>
          )}
        </div>
      )}

      <div className={styles.box}>
        <AskBox
          {...surveyTopic}
          text={surveyTopic.questions[indexQuiz].text}
          indexQuiz={indexQuiz}
        />
        <form
          style={{ maxWidth: "1140px", padding: "0px 20px" }}
          onSubmit={onSubmit}
        >
          {surveyTopic.questions[indexQuiz].groupsQuestionItens
            .sort(orderByItems)
            .map((surveyItem) => {
              const isItemActive =
                isAssistantActive &&
                idSubQuestion === surveyItem.idSubQuestion;
              return (
                <div
                  key={surveyItem.idSubQuestion}
                  className={`${
                    isItemActive
                      ? styles.selectedQuestions
                      : styles.unselectedQuestions
                  } ${styles.subQuestionItem}`}
                >
                  {getQuestionType(surveyItem, isItemActive)}
                </div>
              );
            })}

          <div className={styles.actionButtons}>
            <Button
              handleAction={() => handleGoBack(deactivateAssistant)}
              text="Voltar"
              type="darkTransparent"
              width="170px"
              typeButton="button"
            />

            <Button
              disabled={loading}
              text="Enviar Resposta"
              type="blueWhite"
              width="200px"
              loading={loading}
            />
          </div>
        </form>
      </div>

      <QuizProgressBar
        answeredQuestions={totalSurveyAnswered}
        totalQuestions={totalSurveyQuestions}
      />
    </section>
  );
}

function AskBox({ title, text }: IAskBox) {
  return (
    <div className={styles.askBox}>
      <h1>{title}</h1>
      <div
        dangerouslySetInnerHTML={{
          __html: text ? linkifyHtml(text) : "",
        }}
      />
    </div>
  );
}
